// DriversPanel — list, add, and edit drivers
// login_id and password are required for the driver app login
import { useEffect, useState } from 'react';
import { getDrivers, createDriver, updateDriver } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';

const EMPTY = { name: '', phone: '', license_no: '', login_id: '', password: '' };

export default function DriversPanel() {
  const [drivers, setDrivers] = useState([]);
  const [form,    setForm]    = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [msg,     setMsg]     = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try { setDrivers(await getDrivers()); } catch { setMsg('Failed to load drivers'); }
  }

  function startEdit(d) {
    setEditing(d.id);
    setForm({ name: d.name, phone: d.phone, license_no: d.license_no || '', login_id: d.login_id, password: '' });
  }

  function cancel() { setEditing(null); setForm(EMPTY); setMsg(''); }

  function set(key) { return e => setForm({ ...form, [key]: e.target.value }); }

  async function save(e) {
    e.preventDefault();
    try {
      // Trimmed here, not just at login — a stray trailing space baked into
      // the stored password hash at creation time can never be fixed by
      // trimming later at login, since the hash itself would be wrong.
      const payload = { ...form, login_id: form.login_id.trim(), password: form.password.trim() };
      editing ? await updateDriver(editing, payload) : await createDriver(payload);
      setMsg(editing ? 'Driver updated ✅' : 'Driver added ✅');
      cancel(); load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-1">Drivers</h2>
      <p className="text-sm text-ink-soft mb-4">
        Each driver uses their <strong className="text-ink">Login ID</strong> + <strong className="text-ink">Password</strong> to log in to the driver app.
      </p>

      <Card className="p-5 mb-6 max-w-[440px] flex flex-col gap-3">
        <h3 className="font-display font-bold text-[15px] text-ink m-0">{editing ? 'Edit Driver' : 'Add Driver'}</h3>
        <form onSubmit={save} className="flex flex-col gap-3">
          <label className={labelCls}>Name
            <input className={inputCls} required value={form.name} onChange={set('name')} />
          </label>
          <label className={labelCls}>Phone
            <input className={inputCls} required value={form.phone} onChange={set('phone')} />
          </label>
          <label className={labelCls}>License No.
            <input className={inputCls} value={form.license_no} onChange={set('license_no')} />
          </label>
          <label className={labelCls}>Login ID <span className="text-critical">*</span>
            <input className={inputCls} required value={form.login_id} onChange={set('login_id')}
              placeholder="e.g. driver001" />
          </label>
          <label className={labelCls}>
            {editing ? 'New Password (leave blank to keep current)' : 'Password'} {!editing && <span className="text-critical">*</span>}
            <input className={inputCls} type="password" value={form.password} onChange={set('password')}
              required={!editing} placeholder="Min 6 characters" />
          </label>
          <div className="flex gap-2">
            <Button type="submit">{editing ? 'Update' : 'Add Driver'}</Button>
            {editing && <Button variant="secondary" type="button" onClick={cancel}>Cancel</Button>}
          </div>
          {msg && (
            <p className={`text-sm font-bold px-3 py-2 rounded-s inline-block w-fit ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
              {msg}
            </p>
          )}
        </form>
      </Card>

      <Card>
        <CardHeader title={`Drivers (${drivers.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Phone</th><th className="px-4 py-2.5">License</th>
                <th className="px-4 py-2.5">Login ID</th><th className="px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map(d => (
                <tr key={d.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3 text-ink-soft tabular-nums">{d.id}</td>
                  <td className="px-4 py-3 font-semibold text-ink">{d.name}</td>
                  <td className="px-4 py-3">{d.phone}</td>
                  <td className="px-4 py-3">{d.license_no || '—'}</td>
                  <td className="px-4 py-3"><code className="bg-paper border border-line rounded px-1.5 py-0.5 text-xs">{d.login_id}</code></td>
                  <td className="px-4 py-3"><Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => startEdit(d)}>Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
